import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination, Scrollbar, Keyboard, Mousewheel, Grid } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';

/** Page builder Carousel component */
new Swiper('.swiper-carousel.swiper-carousel-images', {
    modules: [Autoplay, Navigation, Pagination, Scrollbar],
    loop: true,
    pagination: { el: '.swiper-pagination', clickable: true },
    navigation: { prevEl: '.swiper-button-prev', nextEl: '.swiper-button-next' },
});

/** Program days carousel */
let daysSwiper = null;
let selectedDay = null;

const initProgramDaysSelectorCarousel = (day = null) => {
    const containerSel = '#days-swiper-container';
    const swiperSel = '#days-swiper';
    const prevSel = `${containerSel} #days-prev`;
    const nextSel = `${containerSel} #days-next`;

    const el = document.querySelector(containerSel);
    const swiperEl = document.querySelector(swiperSel);
    if (!el || !swiperEl) return;

    const nextDay = day || el.dataset.selectedDay;

    if (!selectedDay || (selectedDay !== nextDay)) {
        selectedDay = nextDay;

        const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
        let startIndex = slides.findIndex(s => s.dataset.date === selectedDay);
        if (startIndex < 0) {
            startIndex = Math.max(0, slides.findIndex(s => s.classList.contains('swiper-slide-active')));
        }

        if (daysSwiper?.destroy) daysSwiper.destroy(true, true);

        const prevBtn = el.querySelector('#days-prev');
        const nextBtn = el.querySelector('#days-next');

        const updateNav = (sw) => {
            const toggle = (btn, disabled) => {
                if (!btn) return;
                btn.toggleAttribute('disabled', disabled);
                btn.classList.toggle('swiper-button-disabled', disabled);
                btn.setAttribute('aria-disabled', String(disabled));
            };
            toggle(prevBtn, sw.isBeginning);
            toggle(nextBtn, sw.isEnd);
        };

        swiperEl.setAttribute('tabindex', '0');
        swiperEl.setAttribute('aria-roledescription', 'carousel');

        daysSwiper = new Swiper(swiperSel, {
            modules: [Navigation, Pagination, Scrollbar, Keyboard, Mousewheel],
            slidesPerView: 1,
            centeredSlides: true,
            spaceBetween: 8,
            initialSlide: startIndex,
            watchOverflow: true,

            navigation: {
                prevEl: prevSel,
                nextEl: nextSel
            },

            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                dynamicBullets: true,
            },

            keyboard: {
                enabled: true,
                onlyInViewport: true,
                pageUpDown: true
            },

            mousewheel: {
                enabled: true,
                forceToAxis: true,
                releaseOnEdges: true,
                thresholdDelta: 10,
                eventsTarget: containerSel
            },

            on: {
                init(sw) {
                    updateNav(sw);
                },
                slideChange(sw) {
                    updateNav(sw);
                    const active = sw.slides[sw.activeIndex];
                    const day = active?.dataset?.date;
                    Livewire.dispatch('toggle-active-day', { day });
                },
                resize(sw) { updateNav(sw); },
            }
        });
    }
};

document.addEventListener('DOMContentLoaded', () => {
    initProgramDaysSelectorCarousel();
    window.addEventListener('resize', () => daysSwiper?.update());
});

Livewire.on('program-reset-days-selector', (e) => {
    setTimeout(function () {
        initProgramDaysSelectorCarousel(e.day);
    }, 100);
});

/**
 * Speakers carousel (Swiper version)
 */
let speakersSwiper = null;
let speakersLastIndex = 0;
let speakersModalOpen = false;

const initSpeakersCarousel = () => {
    const root = document.querySelector('.speakers-list');
    if (!root) return;

    const layout = root.dataset.layout || 'list';
    const rows = Number(root.dataset.rows ?? 2);
    const columns = Number(root.dataset.columns ?? 4);


    const items = Array.from(root.querySelectorAll('.speaker-item'));
    if (!items.length) return;

    const totalSlides = items.length;
    const maxColumns = Math.min(columns, totalSlides);
    const bpCols = (wanted) => Math.min(wanted, maxColumns);

    const swiperContainer = root.querySelector('.speakers-container'); // .swiper container
    const wrapper = root.querySelector('.speakers-content');           // .swiper-wrapper
    if (!swiperContainer || !wrapper) return;

    if (rows > 1) {
        wrapper.classList.add('flex-wrap');
    }

    // List layout: no Swiper, simple click to open modal
    if (layout === 'list') {
        if (window.Livewire) {
            items.forEach((item) => {
                item.addEventListener('click', (e) => {
                    const target = e?.target?.closest?.('.speaker-item');
                    if (!target) return;
                    Livewire.dispatch('openSpeakerModal', { id: target.id });
                });
            });
        }

        return;
    }

    // Carousel layout

    if (speakersSwiper?.destroy) {
        speakersSwiper.destroy(true, true);
    }

    items.forEach((item) => item.classList.add('swiper-slide'));

    wrapper.setAttribute('tabindex', '0');
    wrapper.setAttribute('aria-roledescription', 'carousel');

    const prevBtn = root.querySelector('.js-speakers-prev');
    const nextBtn = root.querySelector('.js-speakers-next');

    const updateUI = (sw) => {
        /* const toggle = (btn, disabled) => {
            if (!btn) return;
        };
        toggle(prevBtn, sw.isBeginning);
        toggle(nextBtn, sw.isEnd); */

    };

    speakersSwiper = new Swiper(swiperContainer, {
        modules: [Navigation, Keyboard, Grid],
        watchOverflow: true,
        observer: true,
        observeParents: true,

        grid: {
            rows: rows,
            fill: 'row',
        },

        slidesPerView: 1,
        spaceBetween: 24,

        breakpoints: {
            571: {
                slidesPerView: bpCols(2),
            },
            771: {
                slidesPerView: bpCols(3),
            },
            991: {
                slidesPerView: bpCols(4),
            },
            1151: {
                slidesPerView: bpCols(columns),
            },
        },

        navigation: {
            prevEl: prevBtn,
            nextEl: nextBtn,
        },

        keyboard: {
            enabled: true,
            onlyInViewport: true,
            pageUpDown: true,
        },

        on: {
            init(sw) {
                updateUI(sw);
                speakersLastIndex = sw.activeIndex;
            },
            slideChange(sw) {
                updateUI(sw);

                const direction =
                    sw.activeIndex > speakersLastIndex
                        ? 'next'
                        : sw.activeIndex < speakersLastIndex
                            ? 'prev'
                            : 'none';

                speakersLastIndex = sw.activeIndex;

                root.dispatchEvent(
                    new CustomEvent('speakers:swipe', {
                        detail: {
                            direction,
                            activeIndex: sw.activeIndex,
                        },
                    }),
                );
            },
            resize(sw) {
                updateUI(sw);
            },
            click(sw, event) {
                if (!sw.allowClick || !window.Livewire) return;
                const speaker = event?.target?.closest?.('.speaker-item');
                if (!speaker) return;
                Livewire.dispatch('openSpeakerModal', { id: speaker.id });
            },
        },
    });

    // Manual mouse wheel: one column per wheel step
    if (!root.__speakersWheelAttached) {
        const onWheel = (e) => {
            if (!speakersSwiper || speakersModalOpen) return;
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

            const atBeginning = speakersSwiper.isBeginning;
            const atEnd = speakersSwiper.isEnd;

            if (e.deltaY > 0) {
                if (atEnd) return;
                e.preventDefault();
                speakersSwiper.slideNext();
            } else {
                if (atBeginning) return;
                e.preventDefault();
                speakersSwiper.slidePrev();
            }
        };

        root.addEventListener('wheel', onWheel, { passive: false });
        root.__speakersWheelAttached = true;
    }

    const modal = document.getElementById('modal-speaker-info');
    if (modal) {
        modal.addEventListener('shown.bs.modal', () => {
            speakersModalOpen = true;
        });

        modal.addEventListener('hidden.bs.modal', () => {
            speakersModalOpen = false;
        });
    }
};

document.addEventListener('DOMContentLoaded', () => {
    initSpeakersCarousel();
});